/**
 * Shared types and constants for the projects API (ZPL-94).
 *
 * Single source of truth for the request/response contract between the
 * API and its clients. Money values are decimal strings (drizzle
 * `numeric` maps to string; never floats).
 */

import type { PropertyChatRefDTO } from "./chat"
import type { UserSummaryDTO } from "./property-memberships"

export const PROJECT_TYPES = ["garden", "construction", "interior"] as const
export type ProjectType = (typeof PROJECT_TYPES)[number]

export const PROJECT_STATUSES = [
	"draft",
	"ready_for_work",
	"in_progress",
	"awaiting_acceptance",
	"accepted",
] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

export const PROJECT_LIMITS = {
	nameMax: 120,
	descriptionMax: 2000,
} as const

export const DEFAULT_PROJECT_CURRENCY = "PLN"

/** Currencies a creation-cost proposal may be denominated in (ISO-4217). */
export const SUPPORTED_PROJECT_CURRENCIES = ["PLN", "EUR", "USD"] as const
export type ProjectCurrency = (typeof SUPPORTED_PROJECT_CURRENCIES)[number]

/**
 * One entry in a project's creation-cost history (ZPL-95). Exactly one
 * of accepted/rejected/superseded is set on a closed proposal; all
 * three null ⇔ pending. Amounts are decimal strings.
 */
export interface CostProposalDTO {
	id: string
	amount: string
	currency: string
	proposedBy: string
	proposedAt: string
	acceptedBy: string | null
	acceptedAt: string | null
	rejectedBy: string | null
	rejectedAt: string | null
	supersededAt: string | null
}

/**
 * Request body for `POST /properties/:propertyId/projects/:projectId/cost-proposals`.
 * Proposing while another proposal is pending supersedes it. Rejected
 * with 409 once the project is paid.
 */
export interface ProposeCostBody {
	/** Decimal string, max 2 fraction digits, e.g. "12500.00". */
	amount: string
	/** One of {@link SUPPORTED_PROJECT_CURRENCIES}; defaults to PLN server-side. */
	currency?: ProjectCurrency
}

/**
 * A project as returned by the API (JSON-serialized — Dates are ISO
 * strings, money is a decimal string e.g. "12500.00").
 */
export interface ProjectDTO {
	id: string
	propertyId: string
	name: string
	description: string | null
	type: ProjectType
	status: ProjectStatus
	estimatedExecutionCost: string | null
	estimatedExecutionCostCurrency: string
	paidAt: string | null
	archivedAt: string | null
	createdBy: string
	createdWithAi: boolean
	createdAt: string
	updatedAt: string
	/** Present on `GET /projects/:id`; absent on the list endpoint. */
	members?: UserSummaryDTO[]
	/** Present on detail + cost endpoints; absent on the list endpoint. */
	pendingCreationCost?: CostProposalDTO | null
	/** Latest accepted creation cost — what the project page shows. */
	acceptedCreationCost?: CostProposalDTO | null
	/**
	 * Present on GET detail for the owner + project members (the chat's
	 * participants); absent for non-member viewers and on the list/cost
	 * endpoints. Single entry: the project's shared chat.
	 */
	chats?: PropertyChatRefDTO[]
}

/**
 * Request body for `POST /properties/:propertyId/projects`.
 * Server enforces: `type === "construction"` requires the parent
 * property to be a house; project type is immutable after creation.
 */
export interface CreateProjectBody {
	name: string
	type: ProjectType
	description?: string
	/** Decimal string, max 2 fraction digits, e.g. "12500.00". */
	estimatedExecutionCost?: string
	/** ISO-4217 alpha code; defaults to PLN server-side. */
	estimatedExecutionCostCurrency?: string
}

/**
 * Request body for `PATCH /properties/:propertyId/projects/:projectId`.
 * At least one field required. `type` is NOT updatable (422).
 * `status: "accepted"` is owner-only.
 */
export interface UpdateProjectBody {
	name?: string
	/** Empty string clears the value (stored as null). */
	description?: string
	status?: ProjectStatus
	/** Empty string clears the value (stored as null). */
	estimatedExecutionCost?: string
	estimatedExecutionCostCurrency?: string
}

/** Request body for `POST /properties/:propertyId/projects/:projectId/members`. */
export interface AddProjectMemberBody {
	userId: string
}

export interface ProjectResponse {
	project: ProjectDTO
}

export interface ProjectListResponse {
	projects: ProjectDTO[]
}

export interface ProjectMembersResponse {
	members: UserSummaryDTO[]
}
